import * as i0 from '@angular/core';
import { ElementRef, PLATFORM_ID, Injectable, Inject, EventEmitter, Component, ChangeDetectionStrategy, ViewEncapsulation, HostBinding, Input, Output, ViewChild, HostListener, Directive, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import { ReplaySubject, merge, fromEvent, NEVER, of, EMPTY, Subject } from 'rxjs';
import { filter, map, expand, delay, takeUntil } from 'rxjs/operators';
import * as i2 from '@smartdesign/angular/button';
import { SmartDesignButtonModule } from '@smartdesign/angular/button';
import { SmartDesignIconModule } from '@smartdesign/angular/icon';

class PopoverService {
    constructor(platformId, document) {
        this.platformId = platformId;
        this.document = document;
        this.triggerChangeDetection$ = new ReplaySubject();
        this.minWidthWithPositionRight = Infinity;
        this.shouldUpdateChangeDetection$ = merge(this.triggerChangeDetection$, this.onPotentialRerendering());
        this.open = false;
        this.position = 'bottom';
        this.styleMaxHeight = null;
    }
    calcOffsetForPositionRight(boxRef, relativeTo) {
        const relativeElement = this.getElement(relativeTo);
        if (!relativeElement || !boxRef || !isPlatformBrowser(this.platformId)) {
            return { offsetX: 0, offsetY: 0 };
        }
        const box = boxRef.nativeElement;
        const { right, top, height } = relativeElement.getBoundingClientRect();
        return {
            offsetX: right,
            offsetY: top + height / 2 - box.offsetHeight / 2,
        };
    }
    onResize(boxRef) {
        if (!boxRef || !isPlatformBrowser(this.platformId)) {
            // called before view is initialized or on server
            return;
        }
        this.styleMaxHeight = `${boxRef.nativeElement.offsetHeight}px`;
        if (this.open) {
            this.triggerChangeDetection$.next();
        }
    }
    getCorrectedPosition(boxRef, relativeTo) {
        if (!this.open || this.position !== 'right' || !relativeTo || !isPlatformBrowser(this.platformId)) {
            return this.position;
        }
        const { offsetX } = this.calcOffsetForPositionRight(boxRef, relativeTo);
        const { offsetWidth } = boxRef.nativeElement;
        let result = offsetX + offsetWidth > this.document.body.offsetWidth ? 'bottom' : 'right';
        if (result === 'right') {
            this.minWidthWithPositionRight = Math.min(this.minWidthWithPositionRight, offsetWidth);
        }
        else if (this.minWidthWithPositionRight === Infinity) {
            // we could not determine it so we're guessing
            this.minWidthWithPositionRight = offsetWidth + 100;
        }
        else if (offsetX + this.minWidthWithPositionRight < this.document.body.offsetWidth) {
            result = 'right';
        }
        return result;
    }
    /**
     * notifies change detection after a potential rerendering took place
     * only applies to position="right"
     */
    onPotentialRerendering() {
        if (isPlatformBrowser(this.platformId)) {
            const shouldUpdatePosition = () => filter(() => this.open && this.position === 'right');
            // original implementation updates position on scroll events
            const onScroll$ = fromEvent(this.document, 'scroll', { passive: true });
            // original implementation updates position every 10ms for 1minute after a click occurred
            const onClick$ = fromEvent(this.document.body, 'click').pipe(shouldUpdatePosition(), repeatEveryTime(10, 60));
            return merge(onScroll$, onClick$).pipe(shouldUpdatePosition(), map(() => undefined));
        }
        else {
            return NEVER;
        }
    }
    getElement(element) {
        if (element instanceof ElementRef) {
            return element.nativeElement;
        }
        if (element instanceof HTMLElement) {
            return element;
        }
        if (typeof element === 'string') {
            return this.document.querySelector(element);
        }
        return null;
    }
}
PopoverService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: PopoverService, deps: [{ token: PLATFORM_ID }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
PopoverService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: PopoverService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: PopoverService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }];
    } });
const repeatEveryTime = (time, count) => (source) => source.pipe(map((value) => [0, value]), expand(([i, value]) => (i < count ? of([i + 1, value]).pipe(delay(time)) : EMPTY)));

var PopoverClasses;
(function (PopoverClasses) {
    PopoverClasses["BLOCK"] = "popover";
    PopoverClasses["MODIFIER_SHOWN"] = "popover--is-shown";
    PopoverClasses["MODIFIER_BOTTOM"] = "popover--bottom";
    PopoverClasses["MODIFIER_TOP"] = "popover--top";
    PopoverClasses["MODIFIER_RIGHT"] = "popover--right";
    PopoverClasses["MODIFIER_ARROW_LEFT"] = "popover--arrow-left";
    PopoverClasses["MODIFIER_ARROW_CENTER"] = "popover--arrow-center";
    PopoverClasses["MODIFIER_ARROW_RIGHT"] = "popover--arrow-right";
})(PopoverClasses || (PopoverClasses = {}));

class PopoverComponent {
    constructor(changeDetectorRef, service) {
        this.changeDetectorRef = changeDetectorRef;
        this.service = service;
        this.destroyed$ = new Subject();
        this.fadingOut = false;
        this.arrowPosition = 'left';
        this.hasClose = true;
        this.selfClosing = false;
        this.popoverClose = new EventEmitter();
    }
    /** @internal */
    get classes() {
        var _a, _b;
        const arrowPosition = (_a = this.arrowPosition) !== null && _a !== void 0 ? _a : 'left';
        const position = (_b = this.position) !== null && _b !== void 0 ? _b : 'bottom';
        return {
            [PopoverClasses.BLOCK]: true,
            [PopoverClasses.MODIFIER_BOTTOM]: position === 'bottom',
            [PopoverClasses.MODIFIER_TOP]: position === 'top',
            [PopoverClasses.MODIFIER_RIGHT]: position === 'right',
            [PopoverClasses.MODIFIER_ARROW_LEFT]: position !== 'right' && arrowPosition === 'left',
            [PopoverClasses.MODIFIER_ARROW_RIGHT]: position !== 'right' && arrowPosition === 'right',
            [PopoverClasses.MODIFIER_ARROW_CENTER]: position !== 'right' && arrowPosition === 'center',
            [PopoverClasses.MODIFIER_SHOWN]: this.open,
        };
    }
    /** @internal */
    get styles() {
        var _a;
        const position = (_a = this.position) !== null && _a !== void 0 ? _a : 'bottom';
        const offset = position === 'right' ? this.service.calcOffsetForPositionRight(this.boxRef, this.relativeTo) : null;
        return {
            display: position === 'right' && !this.relativeTo ? 'inline-block' : 'block',
            position: position === 'right' && !this.relativeTo ? 'relative' : null,
            'max-height': this.open || this.fadingOut || position === 'right' ? this.service.styleMaxHeight : '0',
            left: offset ? `${offset.offsetX}px` : null,
            top: offset ? `${offset.offsetY}px` : null,
        };
    }
    set position(value) {
        this.service.position = value;
    }
    get position() {
        return this.service.getCorrectedPosition(this.boxRef, this.relativeTo);
    }
    set open(value) {
        this.service.open = value;
    }
    get open() {
        return this.service.open;
    }
    ngOnInit() {
        this.service.shouldUpdateChangeDetection$
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.changeDetectorRef.markForCheck());
    }
    ngAfterViewInit() {
        this.updateHeight();
    }
    ngOnChanges(changes) {
        if ('show' in changes && !changes.show.currentValue) {
            this.fadingOut = true;
        }
    }
    ngOnDestroy() {
        this.destroyed$.next();
    }
    /** @internal */
    updateHeight() {
        this.service.onResize(this.boxRef);
    }
    /** @internal */
    onTransitionEnd() {
        if (!this.open) {
            this.fadingOut = false;
            this.changeDetectorRef.markForCheck();
        }
    }
    /** @internal */
    onCloseClick() {
        this.popoverClose.emit();
        if (this.selfClosing) {
            this.open = false;
        }
    }
}
PopoverComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: PopoverComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: PopoverService }], target: i0.ɵɵFactoryTarget.Component });
PopoverComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: PopoverComponent, selector: "sd-popover", inputs: { position: "position", arrowPosition: "arrowPosition", open: "open", hasClose: "hasClose", selfClosing: "selfClosing", relativeTo: "relativeTo" }, outputs: { popoverClose: "popoverClose" }, host: { listeners: { "window:resize": "updateHeight()", "transitionend": "onTransitionEnd()" }, properties: { "class": "this.classes", "style": "this.styles" } }, providers: [PopoverService], viewQueries: [{ propertyName: "boxRef", first: true, predicate: ["box"], descendants: true }], exportAs: ["sdPopover"], usesOnChanges: true, ngImport: i0, template: "<div class=\"popover__box\" #box>\n  <button *ngIf=\"hasClose\"\n          type=\"button\"\n          class=\"btn-close\"\n          sdIconButton\n          [size]=\"50\"\n          (click)=\"onCloseClick()\"\n  >\n    close\n  </button>\n  <p class=\"has-font-75\">\n    <ng-content></ng-content>\n  </p>\n</div>\n", styles: ["@charset \"UTF-8\";.popover{position:fixed;z-index:100;visibility:hidden;opacity:0;transition-timing-function:ease;transition-duration:.45s;transition-property:opacity,visibility,margin}.popover__box{position:relative;box-sizing:border-box;min-width:24rem;max-width:100%;padding:1rem 3.5rem 1rem 1rem;text-align:left;background-color:var(--color-white);border:1px solid var(--color-grey-200);border-radius:.5rem;box-shadow:0 -3px 8px rgba(0,0,0,.04),0 5px 5px rgba(206,132,132,.05),0 15px 30px rgba(50,50,93,.08)}@media screen and (max-width: 767px){.popover__box{min-width:100%}}.popover--is-shown{visibility:visible;opacity:1}.popover .icon-btn{position:absolute;top:.5rem;right:.5rem}.popover--bottom{position:relative;max-height:0;margin-top:0;margin-bottom:0;line-height:0;transition-timing-function:ease;transition-duration:.45s;transition-property:opacity,visibility,max-height,margin-bottom,margin-top}.popover--bottom.popover--is-shown{margin-top:1rem;margin-bottom:1rem}.popover--bottom:before{top:.1875rem;left:1rem;transform:rotate(-45deg)}.popover--bottom:after{top:-.4375rem;left:1.3125rem;transform:rotate(45deg)}.popover--bottom.popover--arrow-right:before{right:1.5rem;left:auto}.popover--bottom.popover--arrow-right:after{right:1.4375rem;left:auto}.popover--bottom.popover--arrow-center{text-align:center}.popover--bottom.popover--arrow-center:before{right:auto;left:auto}.popover--bottom.popover--arrow-center:after{right:auto;left:auto;transform:translate(.3125rem) rotate(45deg)}@media screen and (max-width: 1023px){.popover--bottom.popover--is-shown{margin-bottom:.5rem}}.popover--top{position:relative;max-height:0;margin-top:0;margin-bottom:0;line-height:0;transition-timing-function:ease;transition-duration:.45s;transition-property:opacity,visibility,max-height,margin-bottom,margin-top}.popover--top.popover--is-shown{margin-top:1rem;margin-bottom:1rem}.popover--top:before{bottom:-.9375rem;left:2.375rem;transform:rotate(136deg)}.popover--top:after{bottom:-.5625rem;left:1.25rem;transform:rotate(226deg)}.popover--top.popover--arrow-right:before{right:0;left:auto}.popover--top.popover--arrow-right:after{right:1.375rem;left:auto}.popover--top.popover--arrow-center{text-align:center}.popover--top.popover--arrow-center:before{right:auto;left:auto}.popover--top.popover--arrow-center:after{right:auto;left:auto;transform:translate(-1.125rem) rotate(226deg)}.popover--right{position:fixed;max-width:300px;max-height:none;transform:translate(40px);transition-timing-function:ease;transition-duration:.45s;transition-property:opacity,visibility,transform}.popover--right.popover--is-shown{transform:translate(16px)}.popover--right:before{top:calc(50% + 8px);left:.3125rem;transform:rotate(-136deg)}.popover--right:after{top:calc(50% - 9px);left:-.3125rem;transform:rotate(-45deg)}.popover:before{position:absolute;z-index:5;box-sizing:border-box;width:0;height:0;content:\"\";border-color:var(--color-white) var(--color-white) transparent transparent;border-style:solid;border-width:.5rem;box-shadow:3px -3px 8px rgba(0,0,0,.04),0 0 rgba(206,132,132,.05),0 0 rgba(50,50,93,.08);transform-origin:0 0}.popover:after{position:absolute;width:.75rem;height:.75rem;content:\"\";border-top:1px solid var(--color-grey-200);border-left:1px solid var(--color-grey-200)}.input+.popover--bottom{top:-16px}.input+.popover--bottom.popover--is-shown{margin-top:0;margin-bottom:0}@media screen and (max-width: 1023px){.input+.popover--bottom{top:-.5rem}.input+.popover--bottom.popover--is-shown{margin-bottom:.5rem}}\n"], components: [{ type: i2.IconButtonComponent, selector: "[sdIconButton]", inputs: ["size", "onDark", "blurry", "disabledLook"], exportAs: ["sdIconButton"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: PopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sd-popover', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [PopoverService], exportAs: 'sdPopover', template: "<div class=\"popover__box\" #box>\n  <button *ngIf=\"hasClose\"\n          type=\"button\"\n          class=\"btn-close\"\n          sdIconButton\n          [size]=\"50\"\n          (click)=\"onCloseClick()\"\n  >\n    close\n  </button>\n  <p class=\"has-font-75\">\n    <ng-content></ng-content>\n  </p>\n</div>\n", styles: ["@charset \"UTF-8\";.popover{position:fixed;z-index:100;visibility:hidden;opacity:0;transition-timing-function:ease;transition-duration:.45s;transition-property:opacity,visibility,margin}.popover__box{position:relative;box-sizing:border-box;min-width:24rem;max-width:100%;padding:1rem 3.5rem 1rem 1rem;text-align:left;background-color:var(--color-white);border:1px solid var(--color-grey-200);border-radius:.5rem;box-shadow:0 -3px 8px rgba(0,0,0,.04),0 5px 5px rgba(206,132,132,.05),0 15px 30px rgba(50,50,93,.08)}@media screen and (max-width: 767px){.popover__box{min-width:100%}}.popover--is-shown{visibility:visible;opacity:1}.popover .icon-btn{position:absolute;top:.5rem;right:.5rem}.popover--bottom{position:relative;max-height:0;margin-top:0;margin-bottom:0;line-height:0;transition-timing-function:ease;transition-duration:.45s;transition-property:opacity,visibility,max-height,margin-bottom,margin-top}.popover--bottom.popover--is-shown{margin-top:1rem;margin-bottom:1rem}.popover--bottom:before{top:.1875rem;left:1rem;transform:rotate(-45deg)}.popover--bottom:after{top:-.4375rem;left:1.3125rem;transform:rotate(45deg)}.popover--bottom.popover--arrow-right:before{right:1.5rem;left:auto}.popover--bottom.popover--arrow-right:after{right:1.4375rem;left:auto}.popover--bottom.popover--arrow-center{text-align:center}.popover--bottom.popover--arrow-center:before{right:auto;left:auto}.popover--bottom.popover--arrow-center:after{right:auto;left:auto;transform:translate(.3125rem) rotate(45deg)}@media screen and (max-width: 1023px){.popover--bottom.popover--is-shown{margin-bottom:.5rem}}.popover--top{position:relative;max-height:0;margin-top:0;margin-bottom:0;line-height:0;transition-timing-function:ease;transition-duration:.45s;transition-property:opacity,visibility,max-height,margin-bottom,margin-top}.popover--top.popover--is-shown{margin-top:1rem;margin-bottom:1rem}.popover--top:before{bottom:-.9375rem;left:2.375rem;transform:rotate(136deg)}.popover--top:after{bottom:-.5625rem;left:1.25rem;transform:rotate(226deg)}.popover--top.popover--arrow-right:before{right:0;left:auto}.popover--top.popover--arrow-right:after{right:1.375rem;left:auto}.popover--top.popover--arrow-center{text-align:center}.popover--top.popover--arrow-center:before{right:auto;left:auto}.popover--top.popover--arrow-center:after{right:auto;left:auto;transform:translate(-1.125rem) rotate(226deg)}.popover--right{position:fixed;max-width:300px;max-height:none;transform:translate(40px);transition-timing-function:ease;transition-duration:.45s;transition-property:opacity,visibility,transform}.popover--right.popover--is-shown{transform:translate(16px)}.popover--right:before{top:calc(50% + 8px);left:.3125rem;transform:rotate(-136deg)}.popover--right:after{top:calc(50% - 9px);left:-.3125rem;transform:rotate(-45deg)}.popover:before{position:absolute;z-index:5;box-sizing:border-box;width:0;height:0;content:\"\";border-color:var(--color-white) var(--color-white) transparent transparent;border-style:solid;border-width:.5rem;box-shadow:3px -3px 8px rgba(0,0,0,.04),0 0 rgba(206,132,132,.05),0 0 rgba(50,50,93,.08);transform-origin:0 0}.popover:after{position:absolute;width:.75rem;height:.75rem;content:\"\";border-top:1px solid var(--color-grey-200);border-left:1px solid var(--color-grey-200)}.input+.popover--bottom{top:-16px}.input+.popover--bottom.popover--is-shown{margin-top:0;margin-bottom:0}@media screen and (max-width: 1023px){.input+.popover--bottom{top:-.5rem}.input+.popover--bottom.popover--is-shown{margin-bottom:.5rem}}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: PopoverService }]; }, propDecorators: { classes: [{
                type: HostBinding,
                args: ['class']
            }], styles: [{
                type: HostBinding,
                args: ['style']
            }], position: [{
                type: Input
            }], arrowPosition: [{
                type: Input
            }], open: [{
                type: Input
            }], hasClose: [{
                type: Input
            }], selfClosing: [{
                type: Input
            }], relativeTo: [{
                type: Input
            }], popoverClose: [{
                type: Output
            }], boxRef: [{
                type: ViewChild,
                args: ['box']
            }], updateHeight: [{
                type: HostListener,
                args: ['window:resize']
            }], onTransitionEnd: [{
                type: HostListener,
                args: ['transitionend']
            }] } });

class PopoverToggleDirective {
    onClick() {
        if (!this.sdPopoverToggle) {
            return;
        }
        this.sdPopoverToggle.open = !this.sdPopoverToggle.open;
        if (!this.sdPopoverToggle.open) {
            this.sdPopoverToggle.popoverClose.emit();
        }
    }
}
PopoverToggleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: PopoverToggleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
PopoverToggleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: PopoverToggleDirective, selector: "[sdPopoverToggle]", inputs: { sdPopoverToggle: "sdPopoverToggle" }, host: { listeners: { "click": "onClick()" } }, exportAs: ["sdPopoverToggle"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: PopoverToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sdPopoverToggle]',
                    exportAs: 'sdPopoverToggle',
                }]
        }], propDecorators: { sdPopoverToggle: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class SmartDesignPopoverModule {
}
SmartDesignPopoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SmartDesignPopoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignPopoverModule, declarations: [PopoverComponent, PopoverToggleDirective], imports: [CommonModule, SmartDesignIconModule, SmartDesignButtonModule], exports: [PopoverComponent, PopoverToggleDirective] });
SmartDesignPopoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignPopoverModule, imports: [[CommonModule, SmartDesignIconModule, SmartDesignButtonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PopoverComponent, PopoverToggleDirective],
                    imports: [CommonModule, SmartDesignIconModule, SmartDesignButtonModule],
                    exports: [PopoverComponent, PopoverToggleDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PopoverClasses, PopoverComponent, PopoverService, PopoverToggleDirective, SmartDesignPopoverModule };
